import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {AssistantMessageImagePartActionTilesProps} from './assistant-message-image-part-action-tiles.types';
import styles from './assistant-message-image-part-action-tiles.module.scss';
import {TemplatesForInspirationTile} from './components/templates-for-inspiration-tile/templates-for-inspiration-tile';
import {TemplatesForInspirationTileVariant} from './components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';
import {CreateAVideoFeatureIntroTile} from './components/create-a-video-feature-intro-tile/create-a-video-feature-intro-tile';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {ClickableDiv} from '@Components/clickable-div';
import {Text} from '@Components/text';
import {GA4EventName} from '@Libraries/ga-events';

export function AssistantMessageImagePartActionTiles(props: AssistantMessageImagePartActionTilesProps): ReactElement | null {
  const {state, actions} = useAiChat();
  const actionTileStates = state.getActionTileStates();
  const [isDismissing, setIsDismissing] = useState(false);
  const hasRecommendedTemplates = state.getRecommendedTemplates(props.chatId).length > 0;
  const animationCreationState = state.getAnimationCreationState(props.chatId, props.hashedId);
  const hasVideos = props.hasExistingAnimations === true || (animationCreationState.animations?.length ?? 0) > 0;
  const shouldRenderTemplatesForInspirationTile =
    actionTileStates.isTemplatesForInspirationVisible && props.isLatestCreateMessage && hasRecommendedTemplates && state.isTemplateRedesignFeatureEnabled;
  const shouldRenderCreateAVideoFeatureIntroTile = state.isAiChatCreateAVideoFeatureEnabled && actionTileStates.isCreateAVideoFeatureIntroVisible && props.isFirstImageGeneration && !hasVideos;

  useEffect(() => {
    if (shouldRenderTemplatesForInspirationTile && !state.hasSeenTemplatesTile) {
      actions.markTemplatesTileAsSeen();
    }
  }, [shouldRenderTemplatesForInspirationTile, state.hasSeenTemplatesTile, actions]);

  if (state.areActionTilesHidden) {
    return null;
  }

  if (props.isMessageStreaming && props.isLastMessage) {
    return null;
  }

  const handleDismiss = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_DONTSHOW);
    setIsDismissing(true);
  };

  const handleAnimationEnd = (): void => {
    if (isDismissing) {
      actions.hideActionTilesPermanently();
      setIsDismissing(false);
    }
  };

  const getTemplatesForInspirationTile = (): ReactElement | null => {
    if (!shouldRenderTemplatesForInspirationTile) {
      return null;
    }

    const variant = shouldRenderCreateAVideoFeatureIntroTile ? TemplatesForInspirationTileVariant.HALF : TemplatesForInspirationTileVariant.FULL;
    // Marked as false so the templates tile always renders expanded (skips the collapsed variant).
    // 2026-07-13 — https://app.asana.com/1/2909186782725/project/1207431577110120/task/1216412619809629?focus=true
    // To bring back the collapse behaviour, revert to: state.hasSeenTemplatesTile && !shouldRenderCreateAVideoFeatureIntroTile
    const showCollapsedVersion = false;

    return <TemplatesForInspirationTile chatId={props.chatId} hashedId={props.hashedId} variant={variant} showCollapsed={showCollapsedVersion} />;
  };

  const getCreateAVideoFeatureIntroTile = (): ReactElement | null => {
    if (!shouldRenderCreateAVideoFeatureIntroTile) {
      return null;
    }

    return <CreateAVideoFeatureIntroTile chatId={props.chatId} hashedId={props.hashedId} />;
  };

  const getDontShowAgainCta = (): ReactElement | null => {
    if (!templatesForInspirationTile || state.isSmall) {
      return null;
    }

    return (
      <ClickableDiv className={styles.dontShowAgain} onClick={handleDismiss}>
        <Text className={styles.dontShowAgainText} val={window.i18next.t('pmwjs_dont_show_again')} />
      </ClickableDiv>
    );
  };

  const templatesForInspirationTile = getTemplatesForInspirationTile();
  const createAVideoFeatureIntroTile = getCreateAVideoFeatureIntroTile();

  if (!templatesForInspirationTile && !createAVideoFeatureIntroTile) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${state.isSmall ? styles.mobile : ''} ${isDismissing ? styles.dismissing : ''}`} onAnimationEnd={handleAnimationEnd}>
      <div className={`${styles.tilesContainer} ${state.isSmall ? styles.mobile : ''}`}>
        {templatesForInspirationTile}
        {createAVideoFeatureIntroTile}
      </div>
      {getDontShowAgainCta()}
    </div>
  );
}
